import type { ToolCore } from "@loumy/contracts";
import { useEffect, useMemo } from "react";
import type { ZodTypeAny } from "zod";
import { ApiError } from "../api/client";
import { useSession } from "../auth/session";
import { credits } from "../lib/format";
import { estimateCredits, previewBuildError } from "../pricing/preview";
import { resolveToolMode, type WebToolUI } from "../registry/toolRegistry";
import { defaultsOf } from "../settings/introspect";
import { hiddenSettingsFor } from "../settings/modelControls";
import { ModelPicker } from "../settings/ModelPicker";
import { SchemaForm } from "../settings/SchemaForm";
import { useSubmitJob } from "../state/queries";
import { useUiStore } from "../state/uiStore";
import { Badge, Button, Spinner } from "../ui";

// §11 Settings-Region + Credits. Lädt die ToolUI.SettingsUI (oder den generischen
// SchemaForm-Fallback) mit dem je-Tool-Draft; rechnet die Credits-Vorschau client-seitig
// (build→price, §4) und löst den Submit gegen die interne API aus (§3.7). Der
// verbindliche Hold/Estimate entsteht serverseitig (D34) — die Vorschau ist nur Anzeige.

export function SettingsPanel({ core, ui }: { core: ToolCore; ui: WebToolUI | undefined }) {
  const { status } = useSession();
  const draftStore = useUiStore();
  const submit = useSubmitJob();

  const draft = draftStore.drafts[core.id];

  // Draft mit den Schema-Defaults seeden (einmal je Tool, SSOT: defaultsOf, §3.4/D12).
  useEffect(() => {
    if (!draftStore.drafts[core.id]) {
      draftStore.setDraft(core.id, defaultsOf(core.settingsSchema as unknown as ZodTypeAny));
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [core.id]);

  const value = draft ?? {};
  const setValue = (next: Record<string, unknown>) => draftStore.setDraft(core.id, next);

  // D101: im aktuellen Modell versteckte Felder (inkl. 'model' selbst — rendert der ModelPicker).
  // Rein UI — der Server strippt versteckte Werte ohnehin deterministisch (withModelGating).
  const hidden = useMemo(() => hiddenSettingsFor(core, value), [core, value]);

  // Hochgeladene Inputs je Rolle (D61) — vom MediaPanel im uiStore abgelegt.
  const inputsByRole = useUiStore((s) => s.inputs[core.id]);

  const inputRefs = useMemo(() => {
    const refs = inputsByRole ? Object.values(inputsByRole).flat().map((i) => i.asset) : [];
    return refs.map((a) => ({ id: a.id, kind: a.kind, r2_key: a.r2_key }));
  }, [inputsByRole]);

  const submitInputs = useMemo(() => {
    const out: { assetId: string; role: string }[] = [];
    if (inputsByRole) {
      for (const [role, items] of Object.entries(inputsByRole)) {
        for (const it of items) out.push({ assetId: it.asset.id, role });
      }
    }
    return out;
  }, [inputsByRole]);

  // Pflicht-Eingaben (min>0) müssen erfüllt sein, bevor generiert werden darf (Server prüft erneut).
  const inputsSatisfied = useMemo(
    () => core.inputs.every((req) => (inputsByRole?.[req.role]?.length ?? 0) >= req.min),
    [core.inputs, inputsByRole],
  );

  const estimate = useMemo(() => estimateCredits(core.id, value, inputRefs), [core.id, value, inputRefs]);

  const SettingsUI = ui?.SettingsUI;
  const signedIn = status === "signed-in";
  const canGenerate = signedIn && estimate !== null && !submit.isPending && inputsSatisfied;

  // D76 — Disabled-Grund sichtbar machen: scheitert der Preview-Build (estimate===null), obwohl
  // angemeldet UND Pflicht-Eingaben erfüllt sind, läuft hier DERSELBE Build nochmal, um die echte
  // Fehlermeldung zu zeigen (sonst bliebe der Button kommentarlos deaktiviert — siehe der zuvor
  // still verschluckte maxRefs-Build-Fehler des image-generators). Nur dann berechnet (memoisiert).
  const buildError = useMemo(
    () =>
      signedIn && inputsSatisfied && estimate === null ? previewBuildError(core.id, value, inputRefs) : null,
    [signedIn, inputsSatisfied, estimate, core.id, value, inputRefs],
  );

  return (
    // Natürlicher Fluss (KEIN h-full + interner overflow + sticky Footer): die Form und die
    // Generate-Leiste folgen ihrer Inhaltshöhe. Das Scrollen besorgt der äußere Studio-Container
    // (eine Scroll-Ebene), die Credits/Generieren-Leiste klebt damit NICHT mehr am Viewport-Boden,
    // sondern sitzt direkt unter den Settings (responsiv, je nach Platz darüber).
    <div className="flex flex-col">
      <div className="space-y-4 p-4">
        {/* D101: Modell-Dropdown ÜBER dem Formular — die Meta-Entscheidung steht über den
            Feldern, die sie steuert (Defaults/Sichtbarkeit). Tools ohne models → rendert nichts. */}
        <ModelPicker core={core} value={value} onChange={setValue} />
        {SettingsUI ? (
          <SettingsUI value={value} onChange={setValue} hiddenSettings={hidden} />
        ) : (
          // Fallback: generische Form aus dem Zod-Schema (§3.4-Prinzip, SSOT).
          <SchemaForm
            schema={core.settingsSchema as unknown as ZodTypeAny}
            value={value}
            onChange={setValue}
            hidden={hidden}
          />
        )}
      </div>

      {/* D124 — browser-edit-Tools: KEIN Job-Submit-Block im Web-UI (das Tool rechnet live im
          Browser, gespeichert wird im MediaPanel-Renderer); optional ersetzt FooterUI die Leiste.
          Der Job-Pfad des Tools bleibt für die Public-API bestehen (D51). D132: der Modus darf
          eine Funktion der aktuellen Inputs sein (chroma-key-remover: Video-Input ⇒ submit). */}
      {resolveToolMode(ui, inputsByRole) === "browser-edit" ? (
        ui?.FooterUI ? (
          <div className="border-t border-border p-4">
            <ui.FooterUI />
          </div>
        ) : null
      ) : (
      /* Generate-Leiste (Reference: Footer-Leiste) — die Credit-Anzahl steht im Button selbst. */
      <div className="space-y-3 border-t border-border p-4">
        {submit.isError && (
          <p className="text-xs text-danger">
            {submit.error instanceof ApiError ? submit.error.message : "Submit fehlgeschlagen"}
          </p>
        )}
        {submit.isSuccess && (
          <p className="flex items-center gap-1.5 text-xs text-accent">
            <Badge tone="accent">Job</Badge>
            angelegt: <span className="font-mono">{submit.data.jobId.slice(0, 8)}</span>
          </p>
        )}

        {signedIn && !inputsSatisfied && (
          <p className="text-xs text-muted">Erforderliche Eingabe(n) hochladen, um zu generieren.</p>
        )}

        {/* D76 — WARUM ist der Button deaktiviert? Eine reine Settings-VALIDIERUNG (leeres Pflicht-
            feld wie ein noch leerer Prompt) ist nur „noch nicht fertig" → neutraler Hinweis. Ein
            echter BUILD-Fehler (z.B. zu viele Referenzbilder) bleibt ein roter Fehler. */}
        {buildError?.kind === "validation" && (
          <p className="text-xs text-muted">{buildError.message}, um zu generieren.</p>
        )}
        {buildError?.kind === "build" && (
          <p className="text-xs text-danger">Generierung nicht möglich: {buildError.message}</p>
        )}

        <Button
          variant="primary"
          size="lg"
          icon="sparkles"
          className="w-full"
          disabled={!canGenerate}
          onClick={() =>
            submit.mutate(
              {
                toolId: core.id,
                settings: value,
                inputs: submitInputs.length > 0 ? submitInputs : undefined,
              },
              {
                onSuccess: (res) => {
                  // Den neuen Job öffnen (D65): sein Ergebnis erscheint, sobald er fertig ist (Live-Push, D47).
                  // BEWUSST KEIN clearInputs hier: ein Submit erzeugt IMMER einen neuen, unveränderlichen
                  // Job — ein erneuter „Generieren"-Klick soll SOFORT den nächsten Job starten können
                  // (Kern-Flow D65: generieren → Ergebnis sehen → anpassen → neu generieren). Würden wir
                  // die Inputs hier leeren, fiele `inputsSatisfied` für Pflicht-Eingabe-Tools (spritesheet/
                  // chroma-key, min:1) auf false → der Button wäre deaktiviert, solange der eben
                  // abgeschickte Job processing ist (bis useOpenedJob nachlädt). useOpenedJob rehydriert
                  // die Inputs des neuen Jobs ohnehin (clearInputs+addInput mit presigned URLs, D56/D65) →
                  // ersetzt+revokt dabei die lokalen blob-Previews und hält die Dropzones konsistent
                  // gefüllt. Object-URL-Lifecycle = wie bei nicht-submitteten Inputs (clearInputs/
                  // removeInput/resetUserScopedState revoken); nur ein durch ANHALTENDEN Job-Detail-
                  // Fetch-Fehler verwaister blob bleibt kurz liegen und heilt beim nächsten dieser
                  // Pfade — kein dauerhaftes Leck, kein neuer Leak-Typ (Codex-P1, bewusst).
                  draftStore.openJob(res.jobId);
                },
              },
            )
          }
        >
          {submit.isPending ? (
            <Spinner size={18} />
          ) : signedIn ? (
            estimate ? `Generieren · ${credits(estimate.credits)}` : "Generieren"
          ) : (
            "Anmelden zum Generieren"
          )}
        </Button>
      </div>
      )}
    </div>
  );
}
